import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet } from 'react-router-dom';
import { 
  Home, Users, Calendar, FileText, Receipt, Settings, LogOut, 
  Menu, X, GraduationCap, Bell, MessageSquare, CalendarPlus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { getPortalToken, getPortalUserRaw, clearPortalSession } from './portalStorage';
import { bootstrapPortalPwa } from './portalPwa';
import PortalInstallPrompt from './PortalInstallPrompt';

const navigation = [
  { name: 'Übersicht', href: '/portal/dashboard', icon: Home },
  { name: 'Meine Kinder', href: '/portal/children', icon: Users },
  { name: 'Stundenplan', href: '/portal/schedule', icon: Calendar },
  { name: 'Nachrichten', href: '/portal/messages', icon: MessageSquare, showUnreadBadge: true },
  { name: 'Probestunde anfragen', href: '/portal/trial-lesson', icon: CalendarPlus },
  { name: 'Rechnungen', href: '/portal/invoices', icon: Receipt },
  { name: 'Verträge', href: '/portal/contracts', icon: FileText },
  { name: 'Einstellungen', href: '/portal/settings', icon: Settings },
];

export default function PortalLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    bootstrapPortalPwa();
    const token = getPortalToken();
    const userData = getPortalUserRaw();

    if (!token || !userData) {
      navigate('/portal/login');
      return;
    }

    setUser(JSON.parse(userData));
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }, [navigate]);

  // Poll unread messages count every 30s
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const fetch = () => {
      api.get('/portal/messages/unread-count')
        .then((r) => { if (!cancelled) setUnread(r.data.unread || 0); })
        .catch(() => {});
    };
    fetch();
    const id = setInterval(fetch, 30000);
    window.addEventListener('focus', fetch);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('focus', fetch); };
  }, [user]);

  const handleLogout = () => {
    clearPortalSession();
    navigate('/portal/login');
  };

  const currentPath = window.location.pathname;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-red/10 to-blue-600 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900">Eltern-Portal</h1>
                <p className="text-xs text-slate-500">Musikschule</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = currentPath === item.href || currentPath.startsWith(item.href + '/');
              const showBadge = item.showUnreadBadge && unread > 0;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`portal-nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                    isActive 
                      ? "bg-brand-red/5 text-brand-red" 
                      : "text-slate-600 hover:bg-slate-100"
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="w-5 h-5" />
                  <span className="flex-1">{item.name}</span>
                  {showBadge && (
                    <span
                      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-semibold"
                      data-testid="portal-messages-badge"
                    >
                      {unread > 99 ? '99+' : unread}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User info & Logout */}
          <div className="p-4 border-t border-slate-200">
            {user && (
              <div className="mb-3 px-3">
                <p className="text-sm font-medium text-slate-900">{user.guardian_name}</p>
                <p className="text-xs text-slate-500">{user.email}</p>
              </div>
            )}
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Abmelden
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-3">
              <button className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg relative">
                <Bell className="w-5 h-5" />
                {/* Notification badge */}
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6">
          <PortalInstallPrompt />
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
}
